/**
 * LongWindowAccumulator — Phase 3-D (Slide 20 횡단 원칙).
 *
 * 슬라이드 핵심: "단발 이벤트 ❌ → 30초+ 긴 윈도우에서 누적 패턴".
 * 현재 대부분의 detector가 1-2s sliding window로 동작 → 한순간 이상
 * 행동은 잡지만 30초 단위 누적 패턴은 못 본다.
 *
 * 이 모듈은 임의의 명명된 signal channel에 boolean/scalar sample을
 * push하면 지정된 long window (default 30s) 안의 통계를 반환:
 *   - on-time / total / on-ratio
 *   - 최근 transitions (on→off, off→on)
 *   - 누적 평균 score (scalar 입력 시)
 *
 * 사용 예: 여러 detector의 active 플래그를 다 push하면 30초 동안:
 *   - "downPosture 30s 내 3회, total 4.2s on"
 *   - "head pose unknown 30s 내 12.1s, 비율 40%"
 *   - "팜 hidden 30s 내 8.3s"
 *
 * 슬라이드 20: "그레이 영역 인정 — 안/경계/밖 + duration" 도 이 위에
 * 얹을 수 있다 (threshold 기반).
 */

interface BinSample { t: number; on: boolean }
interface ScalarSample { t: number; v: number }

export interface LongWindowSnapshot {
  /** Window length (ms). */
  windowMs: number;
  /** Sum of (on==true) durations within the window (ms). */
  onMs: number;
  /** Total observed time (ms) within the window. */
  totalMs: number;
  /** onMs / totalMs in [0,1]. */
  onRatio: number;
  /** Number of off→on transitions in the window. */
  transitions: number;
  /** Latest mean of scalar samples (NaN if scalar input never used). */
  scalarMean: number;
  /** Latest peak of scalar samples (NaN if unused). */
  scalarPeak: number;
}

export class LongWindowAccumulator {
  private readonly windowMs: number;
  private bin: BinSample[] = [];
  private scalar: ScalarSample[] = [];

  constructor(windowMs = 30_000) { this.windowMs = windowMs; }

  reset(): void { this.bin = []; this.scalar = []; }

  /** Push a boolean on/off sample at time `t`. */
  pushBool(on: boolean, t: number): void {
    this.bin.push({ t, on });
    this.prune(t);
  }

  /** Push a scalar sample (e.g. confidence, score) at time `t`. */
  pushScalar(v: number, t: number): void {
    this.scalar.push({ t, v });
    this.prune(t);
  }

  /** Snapshot at the given `now` (ms). */
  snapshot(now: number): LongWindowSnapshot {
    this.prune(now);
    let onMs = 0;
    let totalMs = 0;
    let transitions = 0;
    for (let i = 1; i < this.bin.length; i++) {
      const dt = this.bin[i].t - this.bin[i - 1].t;
      if (dt <= 0 || dt > 1000) continue;  // ignore gaps > 1s — treat as out-of-record
      totalMs += dt;
      if (this.bin[i - 1].on) onMs += dt;
      if (!this.bin[i - 1].on && this.bin[i].on) transitions += 1;
    }
    let scalarMean = Number.NaN, scalarPeak = Number.NaN;
    if (this.scalar.length > 0) {
      let s = 0, p = -Infinity;
      for (const x of this.scalar) { s += x.v; if (x.v > p) p = x.v; }
      scalarMean = s / this.scalar.length;
      scalarPeak = p;
    }
    return {
      windowMs: this.windowMs,
      onMs, totalMs,
      onRatio: totalMs > 0 ? onMs / totalMs : 0,
      transitions,
      scalarMean, scalarPeak,
    };
  }

  private prune(now: number): void {
    const cutoff = now - this.windowMs;
    while (this.bin.length && this.bin[0].t < cutoff) this.bin.shift();
    while (this.scalar.length && this.scalar[0].t < cutoff) this.scalar.shift();
  }
}

/**
 * Convenience — manage a set of named channels with one shared window.
 * `pushBool('downPosture', isDownPostureActive)` etc.
 */
export class MultiChannelAccumulator {
  private readonly windowMs: number;
  private chans = new Map<string, LongWindowAccumulator>();

  constructor(windowMs = 30_000) { this.windowMs = windowMs; }

  private ensure(name: string): LongWindowAccumulator {
    let a = this.chans.get(name);
    if (!a) { a = new LongWindowAccumulator(this.windowMs); this.chans.set(name, a); }
    return a;
  }

  pushBool(name: string, on: boolean, t: number): void { this.ensure(name).pushBool(on, t); }
  pushScalar(name: string, v: number, t: number): void { this.ensure(name).pushScalar(v, t); }

  snapshot(now: number): Record<string, LongWindowSnapshot> {
    const out: Record<string, LongWindowSnapshot> = {};
    this.chans.forEach((a, n) => { out[n] = a.snapshot(now); });
    return out;
  }

  reset(): void { this.chans.forEach(a => a.reset()); }
}
